import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import { useOptionalSpaceContext } from '../contexts/SpaceContext';
import type { PlatformEvent } from '../types/events';

const MCP_CONNECTION_SOURCE = 'optr.mcp.connections';

interface UseMcpAuthEventsOptions {
  /** When set, only events for this server fire the handlers. */
  serverId?: string;
  /** Called once when the backend confirms a successful connect for this user. */
  onConnected?: (serverId: string) => void;
  /** Called when the backend reports a failed connect. */
  onFailed?: (serverId: string, message?: string) => void;
}

/**
 * Subscribes to platform events that signal completion of the per-user MCP
 * OAuth flow. When the backend emits `optr.mcp.connections` for the current
 * user, we invalidate the matching `auth-status` query so the UI reflects
 * the new state without a manual refresh, and fire the optional callbacks
 * so callers can show a toast or flip local state.
 */
export function useMcpAuthEvents(opts: UseMcpAuthEventsOptions = {}) {
  const { subscribe } = usePlatformEvents();
  // When invoked from the operator chat surface there is no SpaceProvider,
  // so we read the context optionally. Without a space we still fire the
  // callbacks but skip query-cache invalidation (no auth-status query was
  // ever issued from this surface).
  const space = useOptionalSpaceContext();
  const spaceId = space?.spaceId;
  const qc = useQueryClient();

  // Hold callbacks + filter in refs so handler identity doesn't churn the
  // subscription each time a parent re-renders with new closures.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const unsub = subscribe([{}], (event: PlatformEvent) => {
      if (event.source !== MCP_CONNECTION_SOURCE) return;
      const data = event.data as
        | { serverId?: string; status?: 'connected' | 'failed'; message?: string }
        | undefined;
      const eventServerId = data?.serverId;
      if (!eventServerId) return;

      const filter = optsRef.current.serverId;
      if (filter && filter !== eventServerId) return;

      if (spaceId) {
        qc.invalidateQueries({
          queryKey: ['space', spaceId, 'mcp-servers', eventServerId, 'auth-status'],
        });
      }

      if (data?.status === 'connected') {
        optsRef.current.onConnected?.(eventServerId);
      } else if (data?.status === 'failed') {
        optsRef.current.onFailed?.(eventServerId, data.message);
      }
    });

    return unsub;
  }, [subscribe, qc, spaceId]);
}
